import { useRef, useState, type FormEvent } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import * as Sentry from "@sentry/react";
import { mutate } from "swr";

import Button from "components/Button";
import { LINK_BUTTON_CLASS } from "components/FlowButtons";
import NoticeBanner from "components/NoticeBanner";
import { WizardTextField } from "components/WizardFields";
import { useFirebase } from "firebase";
import { SupportCallLine } from "pages/Login/GrantParts";
import IdentifierForm from "pages/Login/IdentifierForm";
import { LoginGrantConfirm } from "pages/Login/LoginGrantConfirm";
import {
  SESSION_ENDED_COPY,
  type GrantLookup,
} from "pages/Login/login-grant-flow";

import { useTrackLeadFlowEvent } from "../analytics";
import { leadFlowEvents, type SignInSurface } from "../events";
import {
  completionResultOf,
  failedResultOf,
  startResultOf,
  NOT_FOUND_COPY,
  PANEL_TITLE,
  TRY_ANOTHER_CHANNEL_LABEL,
  accountChannelCopy,
  signInFailureCopy,
  type SignInMode,
} from "../signIn";
import { startSignIn, startSignUp } from "../signInClient";
import { nameError } from "../validation";

const SURFACE: SignInSurface = "start";

type Phase =
  | { kind: "identify"; notice: string | null; identifier: string }
  | { kind: "code"; loginGrant: string; lookup: GrantLookup }
  | { kind: "create_account"; signupToken: string }
  | { kind: "use_account_channel" };

function PanelHeading({ title }: { title: string }) {
  return (
    <h2 className="font-serif text-[21px] font-normal text-soil">{title}</h2>
  );
}

function IdentifyForm({
  mode,
  notice,
  identifier: initialIdentifier,
  onStarted,
  onCancel,
}: {
  mode: SignInMode;
  notice: string | null;
  /** What to start the field with, such as what was typed before. */
  identifier: string;
  onStarted: (
    loginGrant: string,
    lookup: GrantLookup,
    identifier: string,
  ) => void;
  onCancel: () => void;
}) {
  const [failure, setFailure] = useState<string | null>(notice);
  const [busy, setBusy] = useState(false);
  const trackEvent = useTrackLeadFlowEvent();

  const submit = async (identifier: string) => {
    setFailure(null);
    setBusy(true);
    trackEvent(leadFlowEvents.signInStarted(mode, SURFACE));
    const answer = await startSignIn(identifier);
    setBusy(false);
    trackEvent(
      leadFlowEvents.signInResult(mode, SURFACE, startResultOf(answer)),
    );
    if (answer.kind === "started") {
      onStarted(answer.loginGrant, answer.lookup, identifier);
      return;
    }
    setFailure(signInFailureCopy(answer.failure));
  };

  return (
    <IdentifierForm
      id="sign-in-identifier"
      heading={<PanelHeading title={PANEL_TITLE[mode]} />}
      notice={failure}
      initialValue={initialIdentifier}
      busy={busy}
      onSubmit={(identifier) => void submit(identifier)}
      buttonLabel={() => "Continue"}
    >
      <div>
        <button type="button" className={LINK_BUTTON_CLASS} onClick={onCancel}>
          Cancel
        </button>
      </div>
    </IdentifierForm>
  );
}

function CreateAccountForm({
  mode,
  signupToken,
  onRestart,
}: {
  mode: SignInMode;
  signupToken: string;
  onRestart: () => void;
}) {
  const trackEvent = useTrackLeadFlowEvent();
  const { auth, signInWithCustomToken } = useFirebase();
  const navigate = useNavigate();
  const location = useLocation();
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [attempted, setAttempted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [mustRestart, setMustRestart] = useState(false);

  const firstError = nameError(firstName, "First name");
  const lastError = nameError(lastName, "Last name");

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (busy) return;
    setAttempted(true);
    if (firstError !== null || lastError !== null) return;
    setBusy(true);
    setFailure(null);
    const answer = await startSignUp({ signupToken, firstName, lastName });
    if (answer.kind === "failed") {
      trackEvent(
        leadFlowEvents.signInResult(
          mode,
          SURFACE,
          failedResultOf(answer.failure),
        ),
      );
      setBusy(false);
      setFailure(signInFailureCopy(answer.failure));
      setMustRestart(answer.failure === "token_invalid");
      return;
    }
    try {
      await signInWithCustomToken(auth, answer.customToken);
    } catch (err) {
      Sentry.captureException(err);
      trackEvent(
        leadFlowEvents.signInResult(mode, SURFACE, failedResultOf("other")),
      );
      setBusy(false);
      setFailure(signInFailureCopy("other"));
      return;
    }
    trackEvent(leadFlowEvents.accountCreated(mode));
    void mutate("/me");
    void mutate("/me/home");
    navigate(location.pathname, { replace: true, state: null });
  };

  return (
    <form onSubmit={(event) => void submit(event)} noValidate>
      <p className="text-base text-oat-700">{NOT_FOUND_COPY}</p>
      <div className="mt-2">
        <PanelHeading title="Create your account" />
      </div>
      <div className="mt-4 flex flex-col gap-4">
        {failure && <NoticeBanner tone="error">{failure}</NoticeBanner>}
        <WizardTextField
          id="sign-up-first-name"
          label="First name"
          value={firstName}
          onChange={setFirstName}
          error={attempted ? firstError : null}
          autoComplete="given-name"
          autoFocus
        />
        <WizardTextField
          id="sign-up-last-name"
          label="Last name"
          value={lastName}
          onChange={setLastName}
          error={attempted ? lastError : null}
          autoComplete="family-name"
        />
        {mustRestart ? (
          <Button variant="continue" fullWidth onClick={onRestart}>
            Start again
          </Button>
        ) : (
          <Button type="submit" variant="continue" fullWidth isLoading={busy}>
            Create account
          </Button>
        )}
      </div>
    </form>
  );
}

function UseAccountChannel({ onRestart }: { onRestart: () => void }) {
  return (
    <div>
      <PanelHeading title="Use your sign-in" />
      <p className="mt-3 text-base text-oat-700">{accountChannelCopy()}</p>
      <div className="mt-4">
        <button type="button" className={LINK_BUTTON_CLASS} onClick={onRestart}>
          {TRY_ANOTHER_CHANNEL_LABEL}
        </button>
      </div>
    </div>
  );
}

/**
 * The first screen's inline sign-in and quote lookup. Whatever is typed is
 * sent a code or link, and what happens next depends only on what the
 * proof shows: an account or a Person signs in, a channel nobody holds
 * creates one, and a channel on an account that signs in another way is
 * told so. Signing in leaves the visitor on the screen; the account area
 * then shows what they can pick up.
 */
export default function SignInPanel({
  mode,
  signupToken,
  onCancel,
}: {
  mode: SignInMode;
  /** A token from a link opened elsewhere, which starts the name form. */
  signupToken: string | null;
  onCancel: () => void;
}) {
  const [phase, setPhase] = useState<Phase>(
    signupToken
      ? { kind: "create_account", signupToken }
      : { kind: "identify", notice: null, identifier: "" },
  );
  const typed = useRef("");
  const trackEvent = useTrackLeadFlowEvent();
  const restart = (notice: string | null = null, identifier: string = "") =>
    setPhase({ kind: "identify", notice, identifier });
  const chooseDifferent = () => {
    trackEvent(leadFlowEvents.useDifferentClicked("start"));
    restart();
  };

  const body = () => {
    switch (phase.kind) {
      case "identify":
        return (
          <IdentifyForm
            mode={mode}
            notice={phase.notice}
            identifier={phase.identifier}
            onStarted={(loginGrant, lookup, identifier) => {
              typed.current = identifier;
              setPhase({ kind: "code", loginGrant, lookup });
            }}
            onCancel={onCancel}
          />
        );
      case "code":
        return (
          <LoginGrantConfirm
            key={phase.loginGrant}
            grant={{ loginGrant: phase.loginGrant }}
            lookup={phase.lookup}
            surface="start_panel"
            embedded
            onInvalid={(stage) => {
              trackEvent(
                leadFlowEvents.sessionEndedViewed({
                  purpose: phase.lookup.purpose,
                  surface: "start_panel",
                  stage,
                  resolution: "look_up",
                }),
              );
              restart(SESSION_ENDED_COPY, typed.current);
            }}
            onChangeDestination={chooseDifferent}
            onCompletion={(completion) => {
              if (completion.kind === "not_found") {
                restart(NOT_FOUND_COPY);
                return;
              }
              trackEvent(
                leadFlowEvents.signInResult(
                  mode,
                  SURFACE,
                  completionResultOf(completion),
                ),
              );
              setPhase(
                completion.kind === "create_account"
                  ? {
                      kind: "create_account",
                      signupToken: completion.signupToken,
                    }
                  : { kind: "use_account_channel" },
              );
            }}
          />
        );
      case "create_account":
        return (
          <CreateAccountForm
            mode={mode}
            signupToken={phase.signupToken}
            onRestart={() => restart()}
          />
        );
      case "use_account_channel":
        return <UseAccountChannel onRestart={chooseDifferent} />;
    }
  };

  return (
    <div className="mt-6 rounded-xl border border-oat-200 bg-white p-5 sm:p-6">
      <div className="mx-auto w-full max-w-sm">
        {body()}
        {phase.kind === "identify" || phase.kind === "use_account_channel" ? (
          <div className="mt-4">
            <SupportCallLine
              kind="first_call"
              surface="start_panel"
              screen="sign_in"
            />
          </div>
        ) : null}
      </div>
    </div>
  );
}
